/**
 * CongesDashboardView Component
 * Simplified view for "Mes Congés" route - focuses on leave balance and personal requests
 *
 * Layout structure:
 * - Metrics Row: 3 StatCards (CP, RTT, Heures Sup)
 * - Requests Table: Personal leave requests with DataTable
 */

import { QueryErrorBoundary } from "@components/common/errors";
import { useLeaveDashboard } from "../hooks/useLeaveDashboard";
import type { LeaveRequest } from "../types";
import { CongesDashboardSkeleton } from "./CongesDashboardSkeleton";
import { CongesMetricsRow } from "./CongesMetricsRow";
import { CongesRequestsTable } from "./CongesRequestsTable";

/**
 * CongesDashboardView Props
 */
interface CongesDashboardViewProps {
  /** Optional className for the root container */
  className?: string;
}

/**
 * Extract requests from the data union type
 */
function getRequests(data: unknown): LeaveRequest[] {
  if (Array.isArray(data)) {
    return data;
  }
  if (
    data &&
    typeof data === "object" &&
    "member" in data &&
    Array.isArray((data as { member: unknown }).member)
  ) {
    return (data as { member: LeaveRequest[] }).member;
  }
  return [];
}

/**
 * Simplified orchestrator component for "Mes Congés" page.
 *
 * Shows:
 * - Leave balance cards (CP, RTT, Heures Sup)
 * - Personal leave requests table
 */
export function CongesDashboardView({ className }: CongesDashboardViewProps) {
  // Fetch dashboard summary data
  const { data, isLoading, error, refetch } = useLeaveDashboard();

  // Loading state
  if (isLoading) {
    return <CongesDashboardSkeleton className={className} />;
  }

  // Error state
  if (error) {
    return (
      <QueryErrorBoundary
        error={error}
        refetch={refetch}
        className="my-4"
        title="Erreur du tableau de bord congés"
      />
    );
  }

  const requests = getRequests(data?.recentDemandes);

  return (
    <div className={`space-y-6 animate-fade-in ${className ?? ""}`}>
      {/* Metrics Row - 3 Leave StatCards */}
      <CongesMetricsRow data={data} />

      {/* Personal Leave Requests Table */}
      <CongesRequestsTable requests={requests} />
    </div>
  );
}
